import React, { useState, useEffect } from 'react';
import { titleize, truncate } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import AddButton from 'components/v2/buttons/AddButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import EditButton from 'components/v2/buttons/EditButton';
import { useGetDocumentLinks, useCreateDocumentLinks, useDeleteDocumentLinks, useHandleUpdateDocumentsLink, } from 'utils/DocumentsHooks';
import { showToast } from 'stores/ToastStore';
import CargomaticCard from 'components/v2/card';
import CargomaticModal from 'components/v2/modal';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import LoadingCircle from 'components/ui/LoadingCircle';
import Table from 'components/v2/Table';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import SopForm from './SopForm';
const pageTestId = 'shipper-sop';
export default function SopDocuments({ userId }) {
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [sop, setSop] = useState(null);
    const { t } = useTranslation();
    const { data, error, refetch } = useGetDocumentLinks({
        foreignKeys: [userId],
    });
    const [deleteDocuments] = useDeleteDocumentLinks();
    const [createDocumentLinks] = useCreateDocumentLinks();
    const { handleUpdateDocumentLink } = useHandleUpdateDocumentsLink();
    useEffect(() => {
        if (data) {
            setItems(data.documentLinks || []);
            setLoaded(true);
        }
        if (error) {
            showToast(t('DOCUMENTS_NOT_FOUND'), t('DOCUMENTS_NOT_FOUND'), 'error');
        }
    }, [data, error]);
    const fetchData = async () => {
        try {
            await refetch();
        }
        catch (err) {
            showToast(t('ERROR_FETCHING_DATA'), t('ERROR_FETCHING_DATA'), 'error');
        }
    };
    const getNewSop = (userId) => {
        return {
            link: 'https://',
            name: '',
            foreignKeys: [userId],
            type: 'SOP',
        };
    };
    const handleAddSop = () => {
        setSop(getNewSop(userId));
        setShowFormModal(true);
    };
    const handleEditSop = (item) => {
        setSop(item);
        setShowFormModal(true);
    };
    const handleDeleteSop = (id) => {
        setSop(id);
        setShowConfirmModal(true);
    };
    const handleHideConfirmModal = () => {
        setShowConfirmModal(false);
        setSop(null);
    };
    const handleHideSopForm = () => {
        setShowFormModal(false);
        setSop(null);
    };
    const handleConfirmDeleteSop = async () => {
        var _a, _b;
        setShowConfirmModal(false);
        try {
            const result = await deleteDocuments(sop);
            if ((_b = (_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a.ids) === null || _b === void 0 ? void 0 : _b.length) {
                setItems((currentItems) => currentItems.filter((item) => item.id !== result.data.ids[0]));
            }
            setSop(null);
            showToast(t('SOP_SUCCESSFULLY_DELETED'), t('SOP_SUCCESSFULLY_DELETED'), 'success');
        }
        catch (e) {
            showToast(t('PROBLEM_DELETING_SOP'), t('PROBLEM_DELETING_SOP'), 'error');
        }
        finally {
            fetchData();
        }
    };
    const handleSaveForm = async (sop) => {
        try {
            let result;
            result = await createDocumentLinks(sop);
            const newItem = (result === null || result === void 0 ? void 0 : result.data) || [];
            if (newItem.length) {
                setItems((currentItems) => [...currentItems, ...newItem]);
            }
            setSop(null);
            setShowFormModal(false);
            showToast(t('SOP_SAVED_SUCCESSFULLY'), t('SOP_SAVED_SUCCESSFULLY'), 'success');
        }
        catch (e) {
            showToast(t('PROBLEM_SAVING_SOP'), t('PROBLEM_SAVING_SOP'), 'error');
        }
        finally {
            fetchData();
        }
    };
    const handleUpdateForm = async (sop) => {
        let result;
        const { name, link, id } = sop[0];
        result = await handleUpdateDocumentLink({ name, link }, id);
        const updatedData = result === null || result === void 0 ? void 0 : result.data;
        if (updatedData) {
            setItems((currentItems) => currentItems.map((item) => {
                if (item.id === updatedData.id) {
                    return updatedData;
                }
                return item;
            }));
        }
        setSop(null);
        setShowFormModal(false);
    };
    const renderSopForm = () => {
        if (!showFormModal) {
            return null;
        }
        return (React.createElement(SopForm, { sop: sop, onCancel: handleHideSopForm, onSave: !(sop === null || sop === void 0 ? void 0 : sop.id) ? handleSaveForm : handleUpdateForm }));
    };
    const renderSopItems = () => {
        if (!loaded) {
            return (React.createElement("div", { className: "p-6", "data-testid": `${pageTestId}-loading` },
                React.createElement(LoadingCircle, { text: t('LOADING_SOP') })));
        }
        if (items.length === 0) {
            return (React.createElement("div", { className: "px-6 pb-6", "data-testid": `${pageTestId}-no-docs-container` },
                React.createElement("p", { className: "mt-0 mb-4 pt-2", "data-testid": `${pageTestId}-no-docs-text` }, t('NO_SOP')),
                renderActionComponentBasedOnRole(React.createElement(AddButton, { title: t('CREATE_NEW_SOP'), onClick: handleAddSop, dataTestId: `${pageTestId}-create-sop-button` }))));
        }
        const tableColumns = [
            {
                field: 'name',
                label: t('NAME'),
                render: (_, item) => (React.createElement("div", { "data-testid": `${pageTestId}-item-name` }, item.name)),
            },
            {
                field: 'link',
                label: t('URL'),
                render: (_, item) => (React.createElement("div", { "data-testid": `${pageTestId}-item-link` },
                    React.createElement("a", { target: "_blank", href: item.link, rel: "noreferrer" }, truncate(item.link, 20)))),
            },
            {
                field: 'action',
                label: t('ACTION', { count: 2 }),
                render: (_, item) => renderActionComponentBasedOnRole(React.createElement("div", { className: "flex items-center", "data-testid": `${pageTestId}-action-button` },
                    React.createElement("div", { className: "flex justify-end" },
                        React.createElement(Dropdown, { useIcon: true },
                            React.createElement(Dropdown.Button, { dataTestId: `${pageTestId}-edit-button`, onClick: () => handleEditSop(item) }, t('EDIT')),
                            React.createElement(Dropdown.Button, { dataTestId: `${pageTestId}-delete-button`, onClick: () => handleDeleteSop(item.id) }, titleize(t('REMOVE'))))))),
            },
        ];
        return (React.createElement("div", { className: "overflow-x-auto", "data-testid": `${pageTestId}-table` },
            React.createElement(Table, { headers: tableColumns, data: items, columns: tableColumns, tableClassName: "min-w-full default-table", bodyClassName: "bg-white", colWidths: [40, 40, 20] })));
    };
    const renderActionComponentBasedOnRole = (children) => {
        return (React.createElement(AuthorizationWrapper, { requiredRoles: ['shipper-onboarding'] }, children));
    };
    return (React.createElement("div", { "data-testid": `${pageTestId}-items` },
        React.createElement(CargomaticCard, { removeHorizontalPadding: true, removeVerticalPadding: true, header: () => (React.createElement("div", { className: "flex items-center justify-between", "data-testid": `${pageTestId}-add` },
                React.createElement("h3", { className: "my-0 text-lg font-medium leading-6" }, t('SOP')),
                renderActionComponentBasedOnRole(React.createElement(AddButton, { title: t('ADD_NEW_SOP'), onClick: handleAddSop, dataTestId: `${pageTestId}-add-button` })))) }, renderSopItems()),
        showFormModal && (React.createElement(CargomaticModal, { show: showFormModal, onHide: handleHideSopForm, title: t('ADD_SOP') }, renderSopForm())),
        showConfirmModal && (React.createElement(ConfirmModal, { show: showConfirmModal, message: t('SOP_DELETION_CONFIRM'), title: t('CONFIRM_DELETION'), onConfirm: handleConfirmDeleteSop, onHide: handleHideConfirmModal }))));
}
